import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Upload, 
  Search,
  FileText,
  Settings
} from 'lucide-react';

export default function AdministrationPage() {
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '', description: '' });
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchMachineTypes();
  }, []);

  const fetchMachineTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/machine-types`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMachineTypes(data);
      } else if (response.status === 403) {
        toast.error('Accès refusé. Réservé aux administrateurs.');
      }
    } catch (error) {
      console.error('Error fetching machine types:', error);
      toast.error('Erreur lors du chargement des types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingType(null);
    setFormData({ name: '', category: '', description: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      category: type.category || '',
      description: type.description || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }

    try {
      const url = editingType 
        ? `${API}/machine-types/${editingType.type_id}`
        : `${API}/machine-types`;
      
      const response = await fetch(url, {
        method: editingType ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingType ? 'Type modifié' : 'Type créé');
        setDialogOpen(false);
        fetchMachineTypes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error saving type:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (typeId, typeName) => {
    if (!window.confirm(`Supprimer le type "${typeName}" ?`)) return;

    try {
      const response = await fetch(`${API}/machine-types/${typeId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Type supprimé');
        fetchMachineTypes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting type:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast.error('Veuillez sélectionner un fichier CSV');
      return;
    }

    try {
      const text = await csvFile.text();
      
      const response = await fetch(`${API}/machine-types/import-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        credentials: 'include',
        body: text
      });

      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        toast.success(`${result.imported} type(s) importé(s)`);
        fetchMachineTypes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'importation');
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error('Erreur lors de l\'importation');
    }
  };

  const downloadCsvTemplate = () => {
    const template = 'name,category,description\nTour CNC,Tournage,Machine de tournage à commande numérique\nFraiseuse,Fraisage,Machine de fraisage\n';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modele_types_machines.csv';
    link.click();
  };

  const filteredTypes = machineTypes.filter(type => 
    type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (type.category && type.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="administration-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Administration
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Gestion des types de machines
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm" data-testid="create-type-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Type
        </Button>
        <Button onClick={() => setCsvDialogOpen(true)} variant="outline" className="rounded-sm border-2">
          <Upload className="w-4 h-4 mr-2" />
          Importer CSV
        </Button>
        <Button onClick={downloadCsvTemplate} variant="outline" className="rounded-sm border-2">
          <FileText className="w-4 h-4 mr-2" />
          Télécharger Modèle CSV
        </Button>
      </div>

      {/* Search */}
      <Card className="border-2 rounded-sm">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Rechercher un type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm border-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Types Table */}
      <Card className="border-2 rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-heading font-semibold text-sm uppercase tracking-tight flex items-center gap-2">
            <Settings className="w-4 h-4 text-primary" />
            Types de Machines ({filteredTypes.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Chargement...</p>
            </div>
          ) : filteredTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Settings className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun type'}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {searchTerm ? 'Aucun type ne correspond à votre recherche' : 'Créez votre premier type de machine'}
              </p>
              {!searchTerm && (
                <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau Type
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono uppercase text-xs">Nom</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Catégorie</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Description</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTypes.map((type) => (
                  <TableRow key={type.type_id} data-testid={`type-row-${type.type_id}`}>
                    <TableCell className="font-medium">{type.name}</TableCell>
                    <TableCell>
                      {type.category ? (
                        <Badge variant="secondary" className="rounded-sm">
                          {type.category}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md truncate text-sm text-muted-foreground">
                      {type.description || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(type)}
                          className="rounded-sm h-8"
                          data-testid={`edit-type-${type.type_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(type.type_id, type.name)}
                          className="rounded-sm h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-type-${type.type_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              {editingType ? 'Modifier le Type' : 'Nouveau Type'}
            </DialogTitle>
            <DialogDescription>
              {editingType ? 'Modifiez les informations du type' : 'Créez un nouveau type de machine'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nom *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Tour CNC"
                  className="rounded-sm border-2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="category">Catégorie</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Tournage"
                  className="rounded-sm border-2"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description du type de machine..."
                  className="rounded-sm border-2"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm">
                {editingType ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">
              Importer des Types (CSV)
            </DialogTitle>
            <DialogDescription>
              Format attendu: name,category,description
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="csv-file">Fichier CSV</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setCsvFile(e.target.files[0]);
                  setImportResult(null);
                }}
                className="rounded-sm border-2"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Colonnes requises: name, category, description
              </p>
            </div>
            
            {importResult && (
              <div className="bg-muted p-4 rounded-sm space-y-2">
                <p className="font-medium">Résultat de l'importation:</p>
                <p className="text-sm">✅ Importés: {importResult.imported}</p>
                <p className="text-sm">⏭️ Ignorés: {importResult.skipped}</p>
                <p className="text-sm">❌ Erreurs: {importResult.errors}</p>
                
                {importResult.details.skipped_messages.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer">Détails ignorés</summary>
                    <ul className="mt-2 space-y-1">
                      {importResult.details.skipped_messages.map((msg, idx) => (
                        <li key={idx}>{msg}</li>
                      ))}
                    </ul>
                  </details>
                )}
                
                {importResult.details.error_messages.length > 0 && (
                  <details className="text-xs text-red-600">
                    <summary className="cursor-pointer">Détails erreurs</summary>
                    <ul className="mt-2 space-y-1">
                      {importResult.details.error_messages.map((msg, idx) => (
                        <li key={idx}>{msg}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setCsvDialogOpen(false);
              setCsvFile(null);
              setImportResult(null);
            }} className="rounded-sm border-2">
              Fermer
            </Button>
            <Button onClick={handleCsvUpload} disabled={!csvFile} className="btn-industrial rounded-sm">
              <Upload className="w-4 h-4 mr-2" />
              Importer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
